<script setup>
import { computed, h, onMounted, onUnmounted, reactive, ref } from 'vue'
import { NButton, NDataTable, NDropdown, NInput, NModal, NSpace, NTag, NText, useDialog } from 'naive-ui'
import GlobalSearch from '@/components/global_inputs/globalSearch.vue'
import CreateApiMerchant from '@/components/modals/api-merchants/createApiMerchant.vue'
import { useStore } from 'vuex'
import { EllipsisVertical } from '@vicons/ionicons5'
import renderIcon from '@/utils/renderIcon'
import { formatDateTime } from '@/utils/formatDateString'
import { stringToSentence } from '@/utils/camelCaseToTitle'
import {
  ApproveWhiteLabelCompany,
  DisapproveWhiteLabelCompany
} from '@/mixins/use-white-label-for-company'

const dialog = useDialog()
const { commit, dispatch, getters } = useStore()

// device width
const deviceWidth = ref(null ?? window.innerWidth)

const updateDeviceWidth = event => {
  deviceWidth.value = event?.target?.innerWidth ?? null
}

const dropdownOptions = (row) => {
  const options = [
    {
      label: 'Approve',
      key: 'approve'
    },
    {
      label: 'Activate',
      key: 'activate'
    },
    {
      label: 'Deactivate',
      key: 'deactivate'
    }
  ]

  return options.filter((option) => {
    const key = option?.key?.toLowerCase()

    if (key === 'approve') {
      return !row.isApproved && !row.isDeactivated // return approve option
    }
    if (key === 'activate') {
      return !row.isApproved && row.isDeactivated // return activate option
    }
    if (key === 'deactivate') {
      return row.isApproved && !row.isDeactivated // return deactivate option
    }
  })
}

const columnOptions = [
  {
    title: h('b', 'Company Name'),
    key: 'companyName',
    render: (row) => {
      return h(
        'section',
        {
          class: 'd-flex flex-column gap-2'
        },
        [
          // company
          h(
            'div',
            {
              class: 'd-flex gap-3 align-items-center'
            },
            [
              // company logo
              h(
                'img',
                {
                  width: 35,
                  height: 35,
                  src: row.companyLogo,
                  class: 'rounded-circle'
                }
              ),

              // company name
              h('span', stringToSentence(row.companyName))
            ]
          ),

          // date created
          h(
            'small',
            `Created: ${formatDateTime(row?.dateCreated)}`
          )
        ]
      )
    }
  },
  {
    title: h('b', 'Phone Number'),
    key: 'phoneNumber'
  },
  {
    title: h('b', 'Email Address'),
    key: 'emailAddress'
  },
  {
    title: h('b', 'IP Address'),
    key: 'ipAdress',
    width: 150,
    render: (row) => row?.ipAddress ?? 'N/A'
  },
  {
    title: h('b', 'Webhook URL'),
    key: 'webHookURL',
    width: 300,
    render: (row) => row?.webHookURL ?? 'N/A'
  },
  {
    title: h('b', 'Status'),
    key: 'status',
    render: (row) => h(
      NTag,
      {
        size: 'medium',
        type: (!row?.isApproved || row?.isDeactivated) ? 'error'
          : !row?.isDeactivated
            ? 'success' : 'default'
      },
      () => (!row.isApproved || row?.isDeactivated) ? 'Inactive' : 'Active'
    )
  },
  {
    title: h('b', 'Comment'),
    key: 'comment',
    width: 300,
    render: (row) => row?.deactivatedComment ?? 'N/A'
  },
  {
    title: h('b', 'Action'),
    key: 'actions',
    // show dropdown option if avs status exists, and not pending
    render: (row) => h(
      NDropdown,
      {
        // dropdown options
        options: dropdownOptions(row),
        // dropdown option selected
        'on-select': key => handleTableRowOptionSelect(row, key)
      },
      // slots for dropdown vnode
      () => h(
        NText,
        {
          size: 'medium'
        },
        renderIcon(EllipsisVertical)
      )
    ),
    // check that device is a PC
    fixed: deviceWidth.value > 1200 ? 'right' : ''
  }
]

const searchQuery = ref('')

const selectedRowData = ref(null)

const loading = ref(false)

const modalOptions = reactive({
  show: false,
  maskClosable: false,
  modalStyle: { width: '' },
  title: '',
  content: '',
  positiveText: '',
  negativeText: ''
})

const responseModalDialogueOptions = reactive({
  show: false,
  actionStatus: computed(() => getters._actionStatus),
  actionMessage: computed(() => getters._actionMessage)
})

const dataSource = computed(() => getters['whiteLabelForCompany/_allWhiteLabelCompanies'])

const filteredDataSource = computed(() => {
  const query = searchQuery.value?.toLowerCase()
  const data = dataSource.value?.data

  if (!data || !query) return data || []

  return data.filter(record => {
    if (!record) return false

    const formattedDate = formatDateTime(record.dateCreated)
    const statusText = record.isDeactivated ? 'inactive' : 'active'

    const searchableFields = [
      record.companyName,
      record.phoneNumber,
      record.emailAddress,
      record.webHookURL,
      formattedDate,
      statusText
    ]

    return searchableFields.some(field =>
      field?.toString().toLowerCase().includes(query)
    )
  })
})

const pagination = reactive({
  page: 1,
  pageSize: 10
})

const toggleMainModal = (title) => {
  modalOptions.modalStyle.width = 'fit-content'
  // -----------------------------------------------------------
  modalOptions.title = title
  modalOptions.positiveText = ''
  modalOptions.negativeText = ''
  // show modal
  modalOptions.show = true
}

const toggleResponseModal = () => {
  // close main modal
  modalOptions.show = false
  // show response modal
  responseModalDialogueOptions.show = true
}

const closeModal = () => {
  // clear response information
  commit('ACTION_STATUS', '')
  commit('ACTION_MESSAGE', '')
  // close global modal prop
  dispatch('dataTableButtonClick/ClearButtonProp')
  // ----------------------------------
  // responseModalDialogueOptions.show = false

  // clear modal information
  modalOptions.show = false
  modalOptions.maskClosable = false
  modalOptions.modalStyle = { width: '' }
  modalOptions.title = ''
  modalOptions.content = ''
  modalOptions.positiveText = ''
  modalOptions.negativeText = ''
}

const handleCreateDataButtonClick = () => {
  modalOptions.content = 'add api merchant'
  modalOptions.modalStyle.background = 'none'
  // toggle modal
  toggleMainModal()
}

const handleTableRowOptionSelect = (row, option) => {
  // update selected row data
  selectedRowData.value = row
  // ---------------------------------
  switch (option) {
    case 'approve':
      handleApproveButtonClick()
      break

    case 'activate':
      handleApproveButtonClick()
      break

    case 'deactivate':
      handleDisapproveButtonClick()
      break
  }
}

const handleApproveButtonClick = (type) => {
  const spinner = ref(false)

  const d = dialog.success({
    showIcon: false,
    closable: false,
    maskClosable: false,
    content: `Approve ${stringToSentence(selectedRowData.value?.companyName)} ?`,
    action: () => h(
      NSpace,
      {
        space: 5
      },
      () => [
        h(
          NButton,
          {
            ghost: true,
            type: 'success',
            disabled: spinner.value,
            onClick: () => {
              // reset selected row data
              selectedRowData.value = null
              // close dialog modal
              d.destroy()
            }
          },
          () => 'Cancel'
        ),
        h(
          NButton,
          {
            type: 'success',
            loading: spinner.value,
            disabled: spinner.value,
            onClick: () => {
              // start spinner
              spinner.value = true

              const payload = {
                whiteLabelForCompanyId: selectedRowData.value?.whiteLabelForCompanyId,
                paginationParams: {
                  searchValue: 'all',
                  pageNumber: pagination.page,
                  pageSize: pagination.pageSize
                }
              }
              console.log(payload)

              return new Promise(() => {
                ApproveWhiteLabelCompany(payload)
                  .finally(() => {
                    // stop spinner
                    spinner.value = false

                    // reset selected row data
                    selectedRowData.value = null
                    // close dialog modal
                    d.destroy()
                    // notification
                    toggleResponseModal()
                  })
              })
            }
          },
          () => 'Approve'
        )
      ]
    )
  })
}

const handleDisapproveButtonClick = () => {
  const model = ref('')
  const spinner = ref(false)

  const updateComment = (comment) => {
    model.value = comment
  }

  const d = dialog.error({
    showIcon: false,
    closable: false,
    maskClosable: false,
    title: `Disapprove ${stringToSentence(selectedRowData.value?.companyName)}`,
    content: () => h(
      NInput,
      {
        type: 'textarea',
        placeholder: 'reason for disapproval',
        'on-update:value': updateComment,
        disabled: spinner.value
      }
    ),
    action: () => h(
      NSpace,
      {
        space: 5
      },
      () => [
        h(
          NButton,
          {
            ghost: true,
            type: 'error',
            disabled: spinner.value,
            onClick: () => {
              // reset selected row data
              selectedRowData.value = null
              // close dialog modal
              d.destroy()
            }
          },
          () => 'Cancel'
        ),
        h(
          NButton,
          {
            type: 'error',
            loading: spinner.value,
            disabled: spinner.value || !model.value,
            onClick: () => {
              // start spinner
              spinner.value = true

              return new Promise(() => {
                const payload = {
                  whiteLabelForCompanyId: selectedRowData.value?.whiteLabelForCompanyId,
                  comment: model.value,
                  paginationParams: {
                    searchValue: 'all',
                    pageNumber: pagination.page,
                    pageSize: pagination.pageSize
                  }
                }

                DisapproveWhiteLabelCompany(payload)
                  .finally(() => {
                    // stop spinner
                    spinner.value = false

                    // reset selected row data
                    selectedRowData.value = null
                    // close dialog modal
                    d.destroy()
                    // notification
                    toggleResponseModal()
                  })
              })
            }
          },
          () => 'Disapprove'
        )
      ]
    )
  })
}

onMounted(() => {
  window.addEventListener('resize', updateDeviceWidth)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateDeviceWidth)
})
</script>

<template>
  <div class="filter-and-table-container">
    <section class="filter-container">
      <!-- input container -->
      <div class="input-container">
        <!-- search -->
        <div class="input search">
          <GlobalSearch
            round
            v-model:value="searchQuery"
            placeholder="search record"
          />
        </div>
      </div>

      <!-- button container -->
      <div class="button-section">
        <div
          class="button-container"
        >
          <NButton
            type="primary"
            :disabled="loading"
            @click="handleCreateDataButtonClick"
          >
            <div class="btn-content">
              <span class="icon">
                <icon icon="mdi:company" />
              </span>
              <span class="text">Create API Merchant</span>
            </div>
          </NButton>
        </div>
      </div>
    </section>

    <!-- table -->
    <section class="table-container">
      <NDataTable
        :remote="true"
        :loading="loading"
        :disabled="loading"
        :columns="columnOptions"
        :data="filteredDataSource"
        :pagination="dataSource?.totalRecords > 1 && {
          ...pagination,
          itemCount: dataSource?.totalRecords,
          showSizePicker: true,
          pageSizes: [5, 10, 20, 50, 100],
          displayOrder: ['size-picker', 'pages']
        }"
        :scrollX="1500"
      />
    </section>
  </div>

  <!-- information modal -->
  <n-modal
    class="global-action-modal"
    :style="modalOptions.modalStyle"
    v-model:show="modalOptions.show"
    :mask-closable="modalOptions.maskClosable"
    :closable="false"
    :show-icon="false"
    preset="dialog"
    :title="modalOptions.title"
    :content="modalOptions.content"
    :positive-text="modalOptions.positiveText"
    :negative-text="modalOptions.negativeText"
    @negative-click="closeModal"
    @onClose="closeModal"
    @esc="closeModal"
  >
    <div>
      <!-- add api merchant -->
      <CreateApiMerchant
        v-if="modalOptions.content?.toLowerCase() === 'add api merchant'"
        @actionCompleted="toggleResponseModal"
        @closeMainModal="closeModal"
      />
    </div>
  </n-modal>

  <!-- response modal -->
  <n-modal
    v-model:show="responseModalDialogueOptions.show"
    :mask-closable="false"
    :closable="false"
    :show-icon="false"
    preset="dialog"
    negative-text="Close"
    @negative-click="closeModal"
    @onClose="closeModal"
  >
    <span :class="[
      responseModalDialogueOptions.actionStatus==='success'?'text-success':'text-danger'
    ]">
      {{responseModalDialogueOptions.actionMessage}}
    </span>
  </n-modal>
</template>

<style lang="scss" scoped>
div.filter-and-table-container {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  row-gap: 35px;

  // filter container
  section.filter-container {
    display: flex;
    row-gap: 37px;
    justify-content: space-between;

    // input and button container
    &>div {
      display: flex;
      row-gap: 20px;
      align-items: center;

      // input container
      &.input-container {
        width: fit-content;
        column-gap: 5px;

        div.input {
          // search
          &.search {
            width: 269px;
          }

          // dropdown
          &.drop-down {
            width: fit-content;

            div.select-and-btn-container {
              display: flex;
              column-gap: 5px;

              // select container
              div.select-container {
                width: 100%;
              }

              // search-btn-container
              div.search-btn-container {
                width: 100%;

                .n-button {
                  width: 100%;
                }
              }
            }
          }
        }
      }

      // button container
      &.button-section {
        column-gap: 15px;

        div.button-container {
          .n-button {
            width: 100%;

            div.btn-content {
              display: flex;
              column-gap: 5px;
              align-items: center;

              span.icon {
                & > * {
                  font-size: 20px;
                }
              }
            }
          }
        }
      }
    }
  }

  // table container
  section.table-container {
    width: 100%;
  }
}

// MEDIA QUERY

// XX-SM (<351px)
@media (max-width: 350.9px) {
  div.filter-and-table-container {
    // filter container
    section.filter-container {
      width: 100%;
      flex-direction: column;
      row-gap: 20px;

      // input and button container
      &>div {
        width: 100%;
        row-gap: 2px;

        // input container
        &.input-container {
          width: 100%;
          flex-direction: column;
          row-gap: 3px;

          div.input {
            width: 100%;

            // search
            &.search {
              width: 100%;
            }

            // dropdown
            &.drop-down {
              width: 100%;

              div.select-and-btn-container {
                width: 100%;
                flex-direction: column;
                row-gap: 4px;
              }
            }
          }
        }

        // button container
        &.button-section {
          column-gap: 5px;
          overflow: auto;

          div.button-container {
            width: 100%;

            .n-button {
              div.btn-content {
                span.icon {
                  & > * {
                    font-size: 15px;
                  }
                }
              }
            }
          }
        }
      }
    }

    // table container
    // section.table-container {}
  }
}
// XX-SM (<351px)
@media (min-width: 351px) and (max-width: 575.9px) {
  div.filter-and-table-container {
    // filter container
    section.filter-container {
      width: 100%;
      flex-direction: column;
      row-gap: 15px;

      // input and button container
      &>div {
        width: 100%;
        row-gap: 2px;

        // input container
        &.input-container {
          width: 100%;
          flex-direction: column;
          row-gap: 3px;

          div.input {
            width: 100%;

            // search
            &.search {
              width: 100%;
            }

            // dropdown
            &.drop-down {
              width: 100%;

              div.select-and-btn-container {
                width: 100%;
                flex-direction: column;
                row-gap: 4px;
              }
            }
          }
        }

        // button container
        &.button-section {
          column-gap: 5px;
          overflow: auto;

          div.button-container {
            width: fit-content;

            .n-button {
              div.btn-content {
                span.icon {
                  & > * {
                    font-size: 15px;
                  }
                }
              }
            }
          }
        }
      }
    }

    // table container
    // section.table-container {}
  }
}
// SM
@media (min-width: 576px) and (max-width: 767.9px) {
  div.filter-and-table-container {
    // filter container
    section.filter-container {
      width: 100%;
      flex-direction: column;
      row-gap: 20px;

      // input and button container
      &>div {
        width: 100%;
        row-gap: 2px;

        // input container
        &.input-container {
          width: 100%;
          row-gap: 3px;

          div.input {
            width: 100%;

            // search
            // &.search {}

            // dropdown
            &.drop-down {
              // width: fit-content;
            width: 100%;

              div.select-and-btn-container {
                width: 100%;
                row-gap: 4px;
              }
            }
          }
        }

        // button container
        &.button-section {
          column-gap: 8px;
          overflow: auto;

          div.button-container {
            width: fit-content;

            .n-button {
              div.btn-content {
                span.icon {
                  & > * {
                    font-size: 15px;
                  }
                }
              }
            }
          }
        }
      }
    }

    // table container
    // section.table-container {}
  }
}
// MD
@media (min-width: 768px) and (max-width: 991.9px) {
  div.filter-and-table-container {
    // filter container
    section.filter-container {
      width: 100%;
      flex-direction: column;
      row-gap: 20px;

      // input and button container
      &>div {
        width: 100%;
        row-gap: 2px;

        // input container
        &.input-container {
          width: 100%;
          row-gap: 3px;

          div.input {
            width: 100%;

            // search
            &.search {
              width: 100%;
            }

            // dropdown
            &.drop-down {
              width: 100%;

              div.select-and-btn-container {
                width: 100%;
                row-gap: 4px;
              }
            }
          }
        }

        // button container
        &.button-section {
          column-gap: 8px;
          overflow: auto;

          div.button-container {
            width: 100%;

            .n-button {
              div.btn-content {
                span.icon {
                  & > * {
                    font-size: 15px;
                  }
                }
              }
            }
          }
        }
      }
    }

    // table container
    // section.table-container {}
  }
}
// LG
@media (min-width: 992px) and (max-width: 1200.9px) {
  div.filter-and-table-container {
    // filter container
    section.filter-container {
      width: 100%;
      flex-direction: column;
      row-gap: 20px;

      // input and button container
      &>div {
        width: 100%;
        row-gap: 2px;

        // input container
        &.input-container {
          width: 100%;
          row-gap: 3px;

          div.input {
            width: 100%;

            // search
            &.search {
              width: 100%;
            }

            // dropdown
            &.drop-down {
              width: 100%;

              div.select-and-btn-container {
                width: 100%;
                row-gap: 4px;
              }
            }
          }
        }

        // button container
        &.button-section {
          column-gap: 10px;
          overflow: auto;

          div.button-container {
            width: 100%;

            .n-button {
              div.btn-content {
                span.icon {
                  & > * {
                    font-size: 15px;
                  }
                }
              }
            }
          }
        }
      }
    }

    // table container
    // section.table-container {}
  }
}
// XL
// @media (min-width: 1201px) {}
</style>
